import { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut, apiSignUp } from 'services/AuthService'
import { apiGetMe } from 'services/ClientsService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import { USER } from 'constants/roles.constant'

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const {
        user,
        session: { token, signedIn },
    } = useSelector((state) => state.auth)

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data) {
                const { access_token, user } = resp.data
                dispatch(onSignInSuccess(access_token))
                if (resp.data.user) {
                    dispatch(setUser(user))
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)

                if (user?.authority?.includes(USER)) {
                    navigate(appConfig.authenticatedUserEntryPath)
                    return { status: 'success', message: '' }
                }

                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        // await apiSignOut()
        handleSignOut()
    }

    const refreshUser = useCallback(async () => {
        try {
            // Only refresh if user is authenticated and is a client (USER role)
            if (!token || !signedIn || !user?.authority?.includes(USER)) {
                return { status: 'skipped', message: 'User is not a client' }
            }

            const resp = await apiGetMe()
            if (resp.data) {
                // Update user data with fresh data from backend, preserving authority
                const updatedUser = {
                    ...resp.data,
                    authority: user.authority || [USER],
                }
                dispatch(setUser(updatedUser))
                return { status: 'success', message: 'User data refreshed' }
            }
            return { status: 'failed', message: 'No data received' }
        } catch (errors) {
            console.error('Error refreshing user data:', errors)
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }, [dispatch, token, signedIn, user?.authority])

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        signOut,
        refreshUser,
        user,
    }
}

export default useAuth
